import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    SLICE_NAME,
    createNewConversation,
    deleteExistingMessage,
    sendNewMessage,
    fetchConversation,
    fetchUserConversationState,
    fetchUserPermissions
} from './service';
import {
    ChatSliceStateSchema,
    ConversationStateSchema,
    CreateConversationResponseSchema,
    MessageActionResponseSchema,
    GetConversationResponseSchema,
    GetUserConversationStateResponseSchema,
    MessageSchema
} from 'src/types/chat';

const createConversationState = (
    overrides?: Partial<ConversationStateSchema>
): ConversationStateSchema => ({
    conversationId: overrides?.conversationId ?? 0,
    entityId: overrides?.entityId ?? '',
    conversationName: overrides?.conversationName ?? '',
    projectId: overrides?.projectId,
    projectName: overrides?.projectName,
    messages: overrides?.messages ?? [],
    isLoading: overrides?.isLoading ?? false,
    conversationError: overrides?.conversationError,
    unreadMessageCount: overrides?.unreadMessageCount ?? 0,
    replyMessage: overrides?.replyMessage,
    lastReadMessageId: overrides?.lastReadMessageId ?? 0,
    viewPageNumber: overrides?.viewPageNumber ?? 0,
    lastNonSearchPageNumber: overrides?.lastNonSearchPageNumber,
    searchTerm: overrides?.searchTerm ?? '',
    hasNextPage: overrides?.hasNextPage ?? false,
    hasPreviousPage: overrides?.hasPreviousPage ?? false
});

const ensureConversationState = (
    state: ChatSliceStateSchema,
    conversationId: number
) => {
    if (!state.conversations[conversationId]) {
        state.conversations[conversationId] = createConversationState({
            conversationId
        });
    }
    return state.conversations[conversationId];
};

const getConversationById = (
    state: ChatSliceStateSchema,
    conversationId?: number | null
) => {
    if (conversationId === undefined || conversationId === null) {
        return null;
    }
    return ensureConversationState(state, conversationId);
};

const initialState: ChatSliceStateSchema = {
    conversations: {},
    activeConversationId: null,
    activeDiscussionsTabId: null,
    isOpen: false,
    permissionsLoading: false,
    targetMessageId: null
};

const ChatSlice = createSlice({
    name: SLICE_NAME,
    initialState,
    reducers: {
        openChat: (
            state,
            action: PayloadAction<{
                conversationId?: number | null;
                entityId?: string;
                projectId?: number | string | null;
                targetMessageId?: number | null;
            }>
        ) => {
            state.isOpen = true;
            state.targetMessageId = action.payload.targetMessageId ?? null;
            const conversationId = action.payload.conversationId ?? null;
            if (conversationId !== null && conversationId !== undefined) {
                state.activeConversationId = conversationId;
                const conversation = ensureConversationState(
                    state,
                    conversationId
                );
                if (action.payload.entityId) {
                    conversation.entityId = action.payload.entityId;
                }
                if (
                    action.payload.projectId !== undefined &&
                    action.payload.projectId !== null
                ) {
                    conversation.projectId = Number(action.payload.projectId);
                }
            }
        },
        openChatAtMessage: (
            state,
            action: PayloadAction<{
                conversationId: number;
                entityId: string;
                messageId: number;
                resultsPerPage: number;
            }>
        ) => {
            const { conversationId, entityId, messageId, resultsPerPage } =
                action.payload;
            const targetPage = Math.max(
                1,
                Math.ceil(messageId / resultsPerPage)
            );
            state.isOpen = true;
            state.targetMessageId = messageId;
            state.activeConversationId = conversationId;
            const conversation = ensureConversationState(state, conversationId);
            conversation.entityId = entityId;
            conversation.viewPageNumber = targetPage;
            conversation.messages = [];
        },
        clearTargetMessage: (state) => {
            state.targetMessageId = null;
        },
        closeChat: (state) => {
            state.isOpen = false;
            state.targetMessageId = null;
            const conversation = getConversationById(
                state,
                state.activeConversationId
            );
            if (!conversation) {
                return;
            }
            conversation.searchTerm = '';
            state.activeConversationId = null;
        },
        setActiveConversation: (
            state,
            action: PayloadAction<number | null>
        ) => {
            state.activeConversationId = action.payload;
            if (
                action.payload !== null &&
                action.payload !== undefined &&
                action.payload >= 0
            ) {
                ensureConversationState(state, action.payload);
            }
        },
        replyMessage: (state, action: PayloadAction<MessageSchema>) => {
            const conversation = getConversationById(
                state,
                action.payload.conversationId
            );
            if (!conversation) return;
            conversation.replyMessage = action.payload;
        },
        resetReplyMessage: (
            state,
            action: PayloadAction<number | undefined>
        ) => {
            const conversation = getConversationById(
                state,
                action.payload ?? state.activeConversationId
            );
            if (!conversation) return;
            conversation.replyMessage = undefined;
        },
        updateMessageStatus: (
            state,
            action: PayloadAction<{
                conversationId: number;
                messageId: number;
                isDeleted: boolean;
            }>
        ) => {
            const conversation = getConversationById(
                state,
                action.payload.conversationId
            );
            if (!conversation || !conversation.messages) return;
            const message = conversation.messages.find(
                ({ id }) => id === action.payload.messageId
            );
            if (message) {
                message.isDeleted = action.payload.isDeleted;
                if (action.payload.isDeleted) {
                    message.text = '';
                }
            }
        },
        markAllMessagesRead: (
            state,
            action: PayloadAction<{ conversationId: number }>
        ) => {
            const conversation = getConversationById(
                state,
                action.payload.conversationId
            );
            if (!conversation) return;
            conversation.unreadMessageCount = 0;
            const msgs = conversation.messages ?? [];
            if (msgs.length > 0) {
                conversation.lastReadMessageId = msgs[msgs.length - 1].id;
            }
        },
        markMessageUnread: (
            state,
            action: PayloadAction<{ conversationId: number; messageId: number }>
        ) => {
            const conversation = getConversationById(
                state,
                action.payload.conversationId
            );
            if (!conversation) {
                return;
            }
            const msgs = conversation.messages ?? [];
            const idx = msgs.findIndex(
                (m) => m.id === action.payload.messageId
            );
            const newLastReadId = idx > 0 ? msgs[idx - 1].id : 0;
            conversation.lastReadMessageId = newLastReadId;
            conversation.unreadMessageCount = msgs.filter(
                (m) => m.id > newLastReadId
            ).length;
        },
        increaseViewPageNumber: (
            state,
            action: PayloadAction<{ conversationId: number }>
        ) => {
            const conversation = getConversationById(
                state,
                action.payload.conversationId
            );
            if (!conversation) return;
            conversation.viewPageNumber = conversation.viewPageNumber + 1;
        },
        decreaseViewPageNumber: (
            state,
            action: PayloadAction<{ conversationId: number }>
        ) => {
            const conversation = getConversationById(
                state,
                action.payload.conversationId
            );
            if (!conversation) return;
            conversation.viewPageNumber = Math.max(
                0,
                conversation.viewPageNumber - 1
            );
            conversation.hasPreviousPage = conversation.viewPageNumber > 0;
        },
        resetViewPageNumber: (
            state,
            action: PayloadAction<{ conversationId: number }>
        ) => {
            const conversation = getConversationById(
                state,
                action.payload.conversationId
            );
            if (!conversation) return;
            conversation.viewPageNumber = 1;
            conversation.hasPreviousPage = false;
        },
        filterMessages: (
            state,
            action: PayloadAction<{ conversationId: number; entityId: string }>
        ) => {
            const conversation = getConversationById(
                state,
                action.payload.conversationId
            );
            if (!conversation) return;
            conversation.viewPageNumber = 1;
            conversation.entityId = action.payload.entityId;
            conversation.hasPreviousPage = false;
        },
        searchMessages: (
            state,
            action: PayloadAction<{ conversationId: number; term: string }>
        ) => {
            const conversation = getConversationById(
                state,
                action.payload.conversationId
            );
            if (!conversation) return;
            const trimmedTerm = action.payload.term.trim();
            const isClearingSearch = trimmedTerm === '';
            const hadSearchTerm =
                conversation.searchTerm &&
                conversation.searchTerm.trim() !== '';

            conversation.searchTerm = trimmedTerm;

            if (isClearingSearch) {
                // When clearing search, restore the last non-search page number
                // This comes from fetchUserConversationState (server's last seen page)
                // or was saved when starting search
                if (
                    conversation.lastNonSearchPageNumber !== undefined &&
                    conversation.lastNonSearchPageNumber > 0
                ) {
                    conversation.viewPageNumber =
                        conversation.lastNonSearchPageNumber;
                } else {
                    // Fallback to current viewPageNumber if lastNonSearchPageNumber wasn't set
                    conversation.viewPageNumber =
                        conversation.viewPageNumber || 1;
                }
            } else {
                // When starting a new search, save current page if we weren't already searching
                // This preserves the page number before search starts
                if (!hadSearchTerm && conversation.viewPageNumber > 0) {
                    conversation.lastNonSearchPageNumber =
                        conversation.viewPageNumber;
                }
                conversation.viewPageNumber = 1;
            }
            conversation.hasPreviousPage = false;
        },
        clearMessages: (
            state,
            action: PayloadAction<{ conversationId: number }>
        ) => {
            const conversation = getConversationById(
                state,
                action.payload.conversationId
            );
            if (!conversation) return;
            conversation.messages = [];
            conversation.hasNextPage = false;
            conversation.hasPreviousPage = false;
        },
        setActiveDiscussionsTab: (
            state,
            action: PayloadAction<{ projectId: string | null }>
        ) => {
            state.activeDiscussionsTabId = action.payload.projectId;
        },
        websocketMessageReceived: (
            state,
            action: PayloadAction<MessageSchema>
        ) => {
            const message = action.payload;
            const conversation = ensureConversationState(
                state,
                message.conversationId
            );

            // Check if message already exists to prevent duplicates
            const existingMessageIds = new Set(
                (conversation.messages ?? []).map((msg) => msg.id)
            );

            if (!existingMessageIds.has(message.id)) {
                // Add new message to the end of the list
                conversation.messages = [
                    ...(conversation.messages ?? []),
                    message
                ];

                // Update unread count if message is newer than last read
                if (message.id > conversation.lastReadMessageId) {
                    conversation.unreadMessageCount =
                        (conversation.unreadMessageCount ?? 0) + 1;
                }
            }
        },
        websocketMessageDeleted: (
            state,
            action: PayloadAction<{ conversationId: number; messageId: number }>
        ) => {
            const { conversationId, messageId } = action.payload;
            const conversation = getConversationById(state, conversationId);

            if (!conversation || !conversation.messages) {
                return;
            }

            const deletedMessage = conversation.messages.find(
                (msg) => msg.id === messageId
            );

            if (deletedMessage) {
                deletedMessage.text = '';
                deletedMessage.isDeleted = true;

                // Update unread count if deleted message was unread
                if (messageId > conversation.lastReadMessageId) {
                    conversation.unreadMessageCount = Math.max(
                        0,
                        (conversation.unreadMessageCount ?? 0) - 1
                    );
                }
            }
        },
        websocketReactionUpdate: (
            state,
            action: PayloadAction<{
                conversationId: number;
                messageId: number;
                reactions: { emoji: string; users: string[] }[];
            }>
        ) => {
            const { conversationId, messageId, reactions } = action.payload;
            const conversation = getConversationById(state, conversationId);
            if (!conversation?.messages) {
                return;
            }
            const message = conversation.messages.find(
                (m) => m.id === messageId
            );
            if (message) {
                message.reactions = reactions;
            }
        },
        websocketFetchConversationFulfilled: (
            state,
            action: PayloadAction<{
                data: GetConversationResponseSchema;
                arg: {
                    conversationId: number;
                    entityId: string | null;
                    query?: string;
                };
            }>
        ) => {
            const { data: response, arg } = action.payload;
            const data = response.data;
            const {
                conversationId: requestedId,
                entityId: requestedEntityId,
                query: requestedQuery
            } = arg;
            if (
                requestedId !== undefined &&
                requestedId !== null &&
                requestedId !== data.id
            ) {
                delete state.conversations[requestedId];
            }
            const conversation = ensureConversationState(state, data.id);
            conversation.isLoading = false;
            conversation.conversationError = undefined;
            conversation.conversationId = data.id;
            conversation.conversationName = data.name;

            const newMessages = data.messages ?? [];
            if (requestedEntityId) {
                conversation.entityId = requestedEntityId;
            } else if (newMessages[0]?.entityId) {
                conversation.entityId = newMessages[0].entityId;
            }

            const lastReadId = conversation.lastReadMessageId;
            const isSearchQuery =
                requestedQuery && requestedQuery.trim() !== '';

            if (
                !conversation.messages ||
                conversation.viewPageNumber === 0 ||
                isSearchQuery
            ) {
                conversation.messages = newMessages;
            } else {
                const existingIds = new Set(
                    (conversation.messages ?? []).map((m) => m.id)
                );
                const toAdd = newMessages.filter((m) => !existingIds.has(m.id));
                conversation.messages.unshift(...toAdd);
                conversation.messages.sort(
                    (a, b) => a.timestampEpochMs - b.timestampEpochMs
                );
            }

            conversation.unreadMessageCount = conversation.messages
                ? conversation.messages.filter((m) => m.id > lastReadId).length
                : 0;
            conversation.hasNextPage = Boolean(data.nextCursor);
            conversation.hasPreviousPage = conversation.viewPageNumber > 0;
            state.activeConversationId = data.id;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(createNewConversation.pending, (state) => {
                state.isOpen = true;
            })
            .addCase(
                createNewConversation.fulfilled,
                (
                    state,
                    action: PayloadAction<CreateConversationResponseSchema>
                ) => {
                    const newConversation =
                        action.payload.data.conversation ?? {};
                    const conversation = ensureConversationState(
                        state,
                        newConversation.id
                    );
                    conversation.conversationId = newConversation.id;
                    conversation.entityId = newConversation.entityId;
                    conversation.conversationName = newConversation.name;
                    conversation.projectId = newConversation.projectId;
                    conversation.messages = newConversation.messages ?? [];
                    conversation.isLoading = false;
                    conversation.conversationError = undefined;
                    conversation.unreadMessageCount = 0;
                    conversation.lastReadMessageId = 0;
                    conversation.viewPageNumber = 1;
                    conversation.hasNextPage = false;
                    conversation.hasPreviousPage = false;
                    state.activeConversationId = newConversation.id;
                    state.isOpen = true;
                }
            )
            .addCase(createNewConversation.rejected, (state, action) => {
                const activeConversation = getConversationById(
                    state,
                    state.activeConversationId
                );
                if (
                    activeConversation &&
                    typeof (action.payload as any)?.error === 'string'
                ) {
                    activeConversation.conversationError = (
                        action.payload as any
                    ).error;
                }
            })
            .addCase(
                fetchConversation.pending,
                (
                    state,
                    action: ReturnType<typeof fetchConversation.pending>
                ) => {
                    const { conversationId, entityId, page } = action.meta.arg;
                    const conversation = ensureConversationState(
                        state,
                        conversationId
                    );
                    conversation.isLoading = true;
                    conversation.conversationError = undefined;
                    conversation.viewPageNumber = page;
                    if (entityId) {
                        conversation.entityId = entityId;
                    }
                    state.activeConversationId = conversationId;
                    state.isOpen = true;
                }
            )
            .addCase(
                fetchConversation.fulfilled,
                (
                    state,
                    action: ReturnType<typeof fetchConversation.fulfilled>
                ) => {
                    const data = action.payload.data;
                    const {
                        conversationId: requestedConversationId,
                        entityId: requestedEntityId,
                        query: requestedQuery
                    } = action.meta.arg;
                    if (
                        requestedConversationId !== undefined &&
                        requestedConversationId !== null &&
                        requestedConversationId !== data.id
                    ) {
                        delete state.conversations[requestedConversationId];
                    }
                    const conversation = ensureConversationState(
                        state,
                        data.id
                    );
                    conversation.isLoading = false;
                    conversation.conversationError = undefined;
                    conversation.conversationId = data.id;
                    conversation.conversationName = data.name;

                    const newMessages = data.messages ?? [];

                    if (requestedEntityId) {
                        conversation.entityId = requestedEntityId;
                    } else if (newMessages[0]?.entityId) {
                        conversation.entityId = newMessages[0].entityId;
                    }

                    const lastReadId = conversation.lastReadMessageId;

                    // For search queries, always replace messages. For pagination, merge messages.
                    const isSearchQuery =
                        requestedQuery && requestedQuery.trim() !== '';
                    if (
                        !conversation.messages ||
                        conversation.viewPageNumber === 0 ||
                        isSearchQuery
                    ) {
                        conversation.messages = newMessages;
                    } else {
                        const existingMessageIds = new Set(
                            (conversation.messages ?? []).map((msg) => msg.id)
                        );
                        const messagesToAdd = newMessages.filter(
                            (msg) => !existingMessageIds.has(msg.id)
                        );
                        if (!conversation.messages) {
                            conversation.messages = [];
                        }
                        conversation.messages.unshift(...messagesToAdd);
                        conversation.messages.sort(
                            (a, b) => a.timestampEpochMs - b.timestampEpochMs
                        );
                    }

                    conversation.unreadMessageCount = conversation.messages
                        ? conversation.messages.filter(
                              (msg) => msg.id > lastReadId
                          ).length
                        : 0;

                    if (!data.nextCursor) {
                        conversation.hasNextPage = false;
                    } else {
                        conversation.hasNextPage = true;
                    }

                    conversation.hasPreviousPage =
                        conversation.viewPageNumber > 0;

                    state.activeConversationId = data.id;
                }
            )
            .addCase(
                fetchConversation.rejected,
                (
                    state,
                    action: ReturnType<typeof fetchConversation.rejected>
                ) => {
                    const conversationId = action.meta.arg.conversationId;
                    const conversation = getConversationById(
                        state,
                        conversationId
                    );
                    if (!conversation) return;
                    conversation.isLoading = false;
                    if (typeof (action.payload as any)?.error === 'string') {
                        conversation.conversationError = (
                            action.payload as any
                        ).error;
                    }
                }
            )
            .addCase(fetchUserPermissions.pending, (state) => {
                state.permissionsLoading = true;
            })
            .addCase(
                fetchUserPermissions.fulfilled,
                (
                    state,
                    action: ReturnType<typeof fetchUserPermissions.fulfilled>
                ) => {
                    state.permissionsLoading = false;
                    const permissions =
                        action.payload?.data?.conversations ?? [];
                    permissions.forEach((permission) => {
                        const conversation = ensureConversationState(
                            state,
                            permission.conversationId
                        );
                        conversation.conversationId = permission.conversationId;
                        conversation.entityId = permission.entityId;
                        conversation.projectId = permission.projectId;
                        conversation.projectName = permission.projectName;
                        conversation.conversationName =
                            permission.conversationName;
                        conversation.lastReadMessageId =
                            permission.lastReadMsg ??
                            conversation.lastReadMessageId;
                        conversation.unreadMessageCount =
                            permission.unreadCount ??
                            conversation.unreadMessageCount;
                    });
                }
            )
            .addCase(fetchUserPermissions.rejected, (state) => {
                state.permissionsLoading = false;
            })
            .addCase(
                fetchUserConversationState.pending,
                (
                    state,
                    action: ReturnType<
                        typeof fetchUserConversationState.pending
                    >
                ) => {
                    const { conversationId } = action.meta.arg as {
                        conversationId?: number;
                    };
                    if (
                        conversationId !== undefined &&
                        conversationId !== null
                    ) {
                        const conversation = ensureConversationState(
                            state,
                            conversationId
                        );
                        conversation.isLoading = true;
                        conversation.conversationError = undefined;
                    }
                }
            )
            .addCase(
                fetchUserConversationState.fulfilled,
                (
                    state,
                    action: PayloadAction<GetUserConversationStateResponseSchema>
                ) => {
                    const data = action.payload.data;
                    const conversation = ensureConversationState(
                        state,
                        data.conversationId
                    );
                    const previousPageNumber = conversation.viewPageNumber;
                    conversation.isLoading = false;
                    conversation.conversationId = data.conversationId;
                    conversation.lastReadMessageId = data.lastReadMessageId;
                    conversation.unreadMessageCount = data.numUnreadMessages;
                    conversation.viewPageNumber = data.pageNumber;
                    // Store the server's last seen page as the non-search page number
                    conversation.lastNonSearchPageNumber = data.pageNumber;
                    conversation.conversationError = undefined;
                    conversation.entityId = data.entityId;
                    // Set hasPreviousPage based on viewPageNumber so scrolling can fetch earlier pages
                    conversation.hasPreviousPage = data.pageNumber > 1;
                    conversation.hasNextPage = data.numUnreadMessages > 0;
                    state.activeConversationId = data.conversationId;

                    // Clear messages if viewPageNumber changed (reopening with different page)
                    // This prevents mixing old messages from previous session with new messages
                    if (
                        previousPageNumber > 0 &&
                        previousPageNumber !== data.pageNumber
                    ) {
                        conversation.messages = [];
                    }
                }
            )
            .addCase(fetchUserConversationState.rejected, (state, action) => {
                const conversation = getConversationById(
                    state,
                    state.activeConversationId
                );
                if (!conversation) return;
                conversation.isLoading = false;
                conversation.unreadMessageCount = 0;
                if (typeof (action.payload as any)?.error === 'string') {
                    conversation.conversationError = (
                        action.payload as any
                    ).error;
                }
            })
            .addCase(
                sendNewMessage.pending,
                (state, action: ReturnType<typeof sendNewMessage.pending>) => {
                    const conversation = ensureConversationState(
                        state,
                        action.meta.arg.conversationId
                    );
                    conversation.isLoading = true;
                    conversation.conversationError = undefined;
                }
            )
            .addCase(
                sendNewMessage.fulfilled,
                (state, action: PayloadAction<MessageActionResponseSchema>) => {
                    const conversation = ensureConversationState(
                        state,
                        action.payload.data.message.conversationId
                    );
                    conversation.isLoading = false;
                    conversation.conversationError = undefined;
                    // Check if message already exists (might have been added via WebSocket)
                    const existingMessageIds = new Set(
                        (conversation.messages ?? []).map((msg) => msg.id)
                    );

                    if (
                        !existingMessageIds.has(action.payload.data.message.id)
                    ) {
                        conversation.messages = [
                            ...(conversation.messages ?? []),
                            action.payload.data.message
                        ];
                    }
                }
            )
            .addCase(
                sendNewMessage.rejected,
                (state, action: ReturnType<typeof sendNewMessage.rejected>) => {
                    const conversation = getConversationById(
                        state,
                        action.meta.arg.conversationId
                    );
                    if (!conversation) return;
                    conversation.isLoading = false;
                    if (typeof (action.payload as any)?.error === 'string') {
                        conversation.conversationError = (
                            action.payload as any
                        ).error;
                    }
                }
            )
            .addCase(
                deleteExistingMessage.pending,
                (
                    state,
                    action: ReturnType<typeof deleteExistingMessage.pending>
                ) => {
                    const conversation = ensureConversationState(
                        state,
                        action.meta.arg.conversationId
                    );
                    conversation.isLoading = true;
                    conversation.conversationError = undefined;
                }
            )
            .addCase(
                deleteExistingMessage.fulfilled,
                (state, action: PayloadAction<MessageActionResponseSchema>) => {
                    const message = action.payload.data.message;
                    const conversation = ensureConversationState(
                        state,
                        message.conversationId
                    );
                    conversation.isLoading = false;
                    conversation.conversationError = undefined;
                    const deletedMessage = conversation.messages?.find(
                        (msg) => msg.id === message.id
                    );
                    if (deletedMessage) {
                        deletedMessage.text = '';
                        deletedMessage.isDeleted = true;
                    }
                    conversation.unreadMessageCount = Math.max(
                        0,
                        (conversation.unreadMessageCount ?? 0) - 1
                    );
                }
            )
            .addCase(
                deleteExistingMessage.rejected,
                (
                    state,
                    action: ReturnType<typeof deleteExistingMessage.rejected>
                ) => {
                    const conversation = getConversationById(
                        state,
                        action.meta.arg.conversationId
                    );
                    if (!conversation) return;
                    conversation.isLoading = false;
                    if (typeof (action.payload as any)?.error === 'string') {
                        conversation.conversationError = (
                            action.payload as any
                        ).error;
                    }
                }
            );
    }
});

export const {
    openChat,
    openChatAtMessage,
    closeChat,
    clearTargetMessage,
    setActiveConversation,
    updateMessageStatus,
    replyMessage,
    resetReplyMessage,
    increaseViewPageNumber,
    decreaseViewPageNumber,
    resetViewPageNumber,
    filterMessages,
    searchMessages,
    clearMessages,
    markAllMessagesRead,
    markMessageUnread,
    setActiveDiscussionsTab,
    websocketMessageReceived,
    websocketMessageDeleted,
    websocketReactionUpdate,
    websocketFetchConversationFulfilled
} = ChatSlice.actions;

export default ChatSlice.reducer;
